import React from "react";
import { Dimensions, Platform, Appearance } from "react-native";
import ENV from "app/utils/ENV";
import { applyColorScheme } from "app/utils/styleSheet";
import prefActions from "app/utils/prefActions";
import textHelpers from "app/utils/textHelpers";
import storageActions from "app/utils/storageActions";
import valueActions from "app/utils/valueActions";

if (ENV.WEB) {
  try {
    // require("app/style.css");
  } catch (e) {}
}

export const StyleContext = React.createContext({});

export class StyleProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      systemDarkMode: this.getSystemDarkMode(),
      schemeMode: props.preferences?.schemeMode || "auto",
      lightTheme: props.preferences?.lightTheme || "parchment",
      darkTheme: props.preferences?.darkTheme || "muted",
      sizes: sizes,
      pensieve: props.pensieve,
      demo: props.demo,
      listLayout: props.listLayout,
      uiColor: null,
      DOUBLE_PRESS_THRESHOLD: 200,
      keyboardAccType: "default",
      selectedText: null,
      selection: null,
      focusedValue: null,
      focusedChapter: null,
      focusedInputRef: null,
      emptyMode: false,
      // Narrative panel is mounted once at the Router root (not per-chapter) so
      // it docks to the viewport without a Modal portal/backdrop, leaving the
      // rest of the app scrollable and interactive while it's open.
      narrativeChapter: null,
      narrativeOnSaved: null,
    };
  }

  componentDidMount = async () => {
    if (ENV.WEB) window.addEventListener("focus", this.refresh);
    if (ENV.WEB)
      window.addEventListener("mousewheel", this.scrollListener, {
        passive: false,
      });

    // react to the OS scheme change the moment it happens; the interval
    // below stays only as a fallback (browsers throttle it in background tabs)
    if (ENV.WEB && window.matchMedia) {
      this.darkModeQuery = window.matchMedia("(prefers-color-scheme: dark)");
      this.darkModeListener = () => this.refresh();
      if (this.darkModeQuery.addEventListener)
        this.darkModeQuery.addEventListener("change", this.darkModeListener);
      else if (this.darkModeQuery.addListener)
        this.darkModeQuery.addListener(this.darkModeListener);
    }
    if (!ENV.WEB) {
      this.appearanceSub = Appearance.addChangeListener(() => this.refresh());
    }

    // let preferences = await storageActions.fetchItem("preferences");
    this.refreshInterval = setInterval(this.refresh, 1000 * 10);
    console.warn("style context mounted");

    // align the static styleSheet module with the effective scheme (prefs may
    // override the system scheme it initialized itself from)
    this.syncStaticColors();
    this.applyCSS();
  };

  componentWillUnmount() {
    if (ENV.WEB) window.removeEventListener("focus", this.refresh);
    if (ENV.WEB) window.addEventListener("mousewheel", this.scrollListener);
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.darkModeQuery) {
      if (this.darkModeQuery.removeEventListener)
        this.darkModeQuery.removeEventListener("change", this.darkModeListener);
      else if (this.darkModeQuery.removeListener)
        this.darkModeQuery.removeListener(this.darkModeListener);
    }
    this.appearanceSub?.remove && this.appearanceSub.remove();
  }

  // effective dark mode: user preference first, system scheme when "auto"
  isDarkMode = () => {
    let { schemeMode, systemDarkMode } = this.state;
    return schemeMode == "dark" || (schemeMode == "auto" && systemDarkMode);
  };

  syncStaticColors = () => {
    applyColorScheme(this.isDarkMode());
  };

  applyCSS = () => {
    if (!ENV.WEB) return;
    let { sizes, schemeMode, systemDarkMode, darkTheme, lightTheme } =
      this.state;

    let darkMode =
      schemeMode == "dark" || (schemeMode == "auto" && systemDarkMode);
    let colorScheme = darkMode ? darkTheme : lightTheme;
    let colors = this.getColors(colorScheme);

    let updateExisting = !!document.getElementById("custom-style");
    let style;
    if (updateExisting) {
      style = document.getElementById("custom-style");
    } else {
      style = document.createElement("style");
      style.id = `custom-style`;
    }
    style.innerHTML = `
        .css-view-1dbjc4n *::-webkit-scrollbar-thumb,
        *::-webkit-scrollbar-thumb {
          background-color: ${colors.trim};
          background-color: ${colors.fill};
          background-color: rgba(0,0,0, 0.06);
        }
        .css-view-1dbjc4n *::-webkit-scrollbar-track,
        *::-webkit-scrollbar-track {
          background-color: ${colors.fill};
          background-color: ${colors.empty};
          background-color: rgba(0,0,0, 0.03);
          background-color: rgba(0,0,0, 0.0);
        }

        @media (prefers-color-scheme: dark) {
          
        }
      `;
    if (!updateExisting) document.head.appendChild(style);
  };

  scrollListener = (event) => {
    // Read from instance fields, not state: hovered-moment tracking must not
    // setState on the provider, or every context consumer re-renders per hover.
    let hoveredMoment = this.hoveredMoment;
    let onHoveredScroll = this.onHoveredScroll;
    if (!!hoveredMoment?.id) {
      // if (typeof hoveredMoment.id != "string") {
      // console.log(event.deltaY, hoveredMoment);
      if (onHoveredScroll) {
        onHoveredScroll(event.deltaY);
      }
      // }
      event.preventDefault();
    } else {
      // console.log(event.deltaY);
    }
  };

  render() {
    let { sizes, schemeMode, systemDarkMode, darkTheme, lightTheme } =
      this.state;

    let darkMode =
      schemeMode == "dark" || (schemeMode == "auto" && systemDarkMode);
    let colorScheme = darkMode ? darkTheme : lightTheme;
    let colors = this.getColors(colorScheme);

    if (typeof this.props.children == "object")
      return (
        <StyleContext.Provider
          value={{
            ...this.state,
            // sizes,
            colors,
            colorScheme,
            styleSheet: styleSheet(colors),
            setSchemeMode: (val) => this.updatePrefs("schemeMode", val),
            setLightTheme: (val) => this.updatePrefs("lightTheme", val),
            setDarkTheme: (val) => this.updatePrefs("darkTheme", val),
            setHoveredMoment: (moment, onHoveredScroll = null) => {
              this.hoveredMoment = moment;
              this.onHoveredScroll = onHoveredScroll;
            },
            // setDarkTheme: (darkTheme) => this.setState({ darkTheme }),
            refresh: this.refresh,
            togglePensieveMode: () => {
              storageActions.saveItem("pensieve", !this.state.pensieve);
              this.setState({ pensieve: !this.state.pensieve });
            },
            toggleDemoMode: () => {
              storageActions.saveItem("demo", !this.state.demo);
              this.setState({ demo: !this.state.demo });
              location.reload();
            },
            toggleListLayout: () => {
              storageActions.saveItem("listLayout", !this.state.listLayout);
              this.setState({ listLayout: !this.state.listLayout });
            },
            setUiColor: (uiColor) => {
              this.setState({ uiColor });
            },
            setContext: (key, val) => {
              console.log(`setting context ${key} to ${val}`);
              this.setState({ [key]: val });
            },
            toggleEmptyMode: () => {
              this.setState({ emptyMode: !this.state.emptyMode });
            },
            openNarrative: (chapter, onSaved = null) =>
              this.setState({
                narrativeChapter: chapter,
                narrativeOnSaved: onSaved,
              }),
            closeNarrative: () =>
              this.setState({
                narrativeChapter: null,
                narrativeOnSaved: null,
              }),
            emptyMode: this.state.emptyMode,
            pensieve: this.state.pensieve,
            demo: this.state.demo,
            listLayout: this.state.listLayout,
            uiColor: this.state.uiColor,
            // pensieve: false,
            createFromSelection: this.createFromSelection,
          }}
        >
          {this.props.children}
        </StyleContext.Provider>
      );
    else return <View style={{ backgroundColor: "orange", flex: 1 }} />;
  }

  updatePrefs = async (key, val) => {
    await this.setState({ [key]: val });
    this.syncStaticColors();
    this.applyCSS();
    let pref = { [textHelpers.toSnakeCase(key)]: val };
    let res = await prefActions.update(pref);
  };

  createFromSelection = async () => {
    let {
      selection,
      selectedText,
      focusedValue,
      focusedChapter,
      focusedInputRef,
    } = this.state;

    if (!!selection && !!selectedText && !!focusedInputRef) {
      let child = await valueActions.create(
        {
          is_note: true,
          title: selectedText.trim(),
          parent_ids: focusedValue?.id ? [focusedValue.id] : [],
        },
        focusedChapter?.id,
      );
      if (!!child?.id) {
        // let text = focusedValue.text;
        let text = focusedInputRef.stringVal;
        text = `${text.slice(0, selection[0])}{${child.id}}${text.slice(
          selection[1],
        )}`;
        // console.warn(
        //   "AFTER",
        //   focusedInputRef.stringVal,
        //   text,
        //   selection,
        //   child.id
        // );
        focusedInputRef.onChangeText(text);

        this.setState({ selectedText: null, selection: null });
        // this.props.deselectAll && this.props.deselectAll();
      }
    }
  };

  refresh = async () => {
    // console.log("refresh style context");
    let newSystemDarkMode = this.getSystemDarkMode();
    let { width, height } = Dimensions.get("window");
    let darkModeChanged = newSystemDarkMode != this.state.systemDarkMode;
    let sizeChanged =
      width != this.state.sizes.WIDTH || height != this.state.sizes.HEIGHT;

    // Bail out unless something observable changed — an unconditional
    // setState here re-renders every StyleContext consumer in the app
    // on each interval tick.
    if (!darkModeChanged && !sizeChanged) return;

    await this.setState({
      systemDarkMode: newSystemDarkMode,
      sizes: {
        ...this.state.sizes,
        WIDTH: width,
        HEIGHT: height,
      },
    });
    if (darkModeChanged) {
      // resync everything that captured the scheme at load: ENV.DARK, the
      // static styleSheet colors, and the injected scrollbar CSS
      ENV.refresh();
      this.syncStaticColors();
      this.applyCSS();
    }
  };

  getColors = (colorScheme) => {
    let colors = {};

    // dark themes
    if (colorScheme == "muted") colors = darkColors;
    else if (colorScheme == "bold") colors = { ...darkColors, darkenBy: 0.8 };
    else if (colorScheme == "newspaper_dark")
      colors = {
        empty: "#282828",
        fill: "#191919",
        foreground: "#ddd",
        trim: "#070707",
        subtle: "#444",
        active: "#777",
        activeHighlight: "#1fc",
        // heatmap: '#201',
        heatmap: "#222222",
        darkenBy: 0.6,
        // darkenBy: 1,
      };
    // light themes
    else if (colorScheme == "parchment") colors = lightColors;
    else if (colorScheme == "clarity")
      colors = {
        empty: "#fff",
        fill: "#f2f4f5",
        fill: "#eee",
        foreground: "#444",
        trim: "#e9ebef",
        trim: "#ddd",
        subtle: "#eee",
        active: "#333",
        activeHighlight: "#1fc",
        heatmap: "#222222",
        darkenBy: 0.5,
        // darkenBy: 1,
        darkForeground: "#444",
        lightForeground: "#fff",
      };
    else if (colorScheme == "newspaper_light")
      colors = {
        empty: "#ddd",
        fill: "#ccc",
        foreground: "#5f5f5f",
        trim: "#888",
        subtle: "#444",
        active: "#555",
        activeHighlight: "#1fc",
        // heatmap: '#201',
        heatmap: "#222222",
        darkenBy: 0.5,
        // darkenBy: 1,
      };

    // return { ...colors, ...universalColors };
    return { ...universalColors, ...colors };
  };

  getSystemDarkMode = () => {
    let systemDarkMode = false;
    if (!ENV.WEB) {
      if (Appearance.getColorScheme() === "dark") {
        systemDarkMode = true;
      }
    }
    try {
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
      ) {
        systemDarkMode = true;
      }
    } catch (e) {
      systemDarkMode = true;
    }
    return systemDarkMode;
  };
}

const WIDTH = Dimensions.get("window").width;
const HEIGHT = Dimensions.get("window").height;
// // compared to designed on iphone xs max
// // const screenRatio = Math.min(1.3, WIDTH / 414);
// // export const screenRatio = Math.min(1.3, WIDTH / 1000);
// export const screenRatio = Math.min(1.3, WIDTH / 400);
// const verticalRatio = Math.min(1.3, HEIGHT / 896);
// const aspectRatio = HEIGHT / WIDTH;
// // iphone 5
// const smallScreen = WIDTH <= 320;
// const shortScreen = HEIGHT <= 568;
// const DARK = ENV.DARK;
// const phi = 1.61803399;
// const inversePhi = 0.618033988;
const mobileIncrease = ENV.WEB ? 1 : 2.0;
const largeFactor = 1.2;
const smallFactor = 0.9;

// const RATIO7 = 0.377964473;
// const RATIO7i = 2.645751311;
// let CHILD_RATIO = RATIO7;
// let PARENT_RATIO = RATIO7i;
// CHILD_RATIO = 0.61;
// PARENT_RATIO = 1.15;

const universalColors = {
  darkest: `#020202`,
  lightForeground: `#eeeeee`,
  darkForeground: "#7a6e69",
  lightest: `#f0f0f0`,
  transparent: `rgba(0,0,0, 0)`,
  inputBg: `rgba(50,50,50, 0.5)`,
  buttonBg: `rgba(200,200,200, 0.3)`,
  placeholder: "#666666",
  lightPlaceholder: "#aaaaaa",
  highlight: "rgba(200,200,200,0.5)",
};

const darkColors = {
  empty: universalColors.darkest,
  // empty: '#011420',
  // empty: '#00101d',
  fill: "#222222",
  // fill: '#202a30',
  foreground: universalColors.lightForeground,
  trim: "#444444",
  subtle: "#333333",
  active: "#4ad",
  activeHighlight: "#1fc",
  // heatmap: '#201',
  heatmap: "#222222",
  darkenBy: 0.5,
};
const lightColors = {
  // empty: universalColors.lightest,
  // fill: '#dddddd',
  // foreground: '#444',
  // trim: '#999',
  // subtle: '#aaa',

  // empty: '#fff1ee',
  empty: "#f9ebdd",
  // empty: '#fff5f0',
  fill: "#f8ddd1",
  // fill: '#f8ede1',
  // fill: '#f5eade',
  // foreground: "#7a6e69",
  foreground: universalColors.darkForeground,
  inactive: "#af9f98",
  // trim: '#e8cdc1',
  trim: "#e3cabe",
  // subtle: '#aa9c95',
  subtle: "#e3cabe",

  active: "#c691e5",
  activeHighlight: "#e060ff",
  // heatmap: '#b9a',
  heatmap: "#ccc",
  darkenBy: 0.5,
};
// let allColors = ENV.DARK ? darkColors : lightColors;
// allColors = { ...allColors, ...universalColors };
// export const colors = allColors
// export const colors = ENV.DARK
//   ? { ...darkColors, ...universalColors }
//   : { ...lightColors, ...universalColors };

// export const getColors = () => {
//   return ENV.DARK()
//     ? { ...darkColors, ...universalColors }
//     : { ...lightColors, ...universalColors };
// };

const sizes = {
  tiny: 3,
  small: 5,
  base: 10,
  smallFont: 12,
  large: 20,
  giant: 40,
  extraGiant: 60,
  smallItemHeight: 15,
  default: 15,
  lineHeightRatio: 1.4,
  itemHeight: 30,
  smallBorderRadius: 5,
  borderRadius: 10,
  // borderRadius: 5,
  // bulletWidth: ,
  bulletWidth: 20 + 3 * 2, // sizes.large + (sizes.tiny*2)
  largeBulletWidth: (20 + 3 * 2) * largeFactor,
  WIDTH,
  HEIGHT,
  COMPOSER_HEIGHT: 110,
  COMPOSER_HEIGHT: 0,
  mobileIncrease,
};

const styleSheet = (colors) => ({
  input: {
    fontSize: sizes.default,
    minHeight: sizes.default,
    // minWidth: sizes.giant,
    // backgroundColor: colors.inputBg,
    color: colors.foreground,
    // marginBottom: sizes.base,
    borderWidth: 0,
    fontFamily: "Helvetica",
  },
  //   icon: {
  //     // fontSize: sizes.smallItemHeight,
  //     fontSize: sizes.itemHeight,
  //     // width: sizes.itemHeight,
  //     padding: sizes.base,
  //     color: colors.foreground,
  //     // marginHorizontal: sizes.small,
  //   },
  //   bullet: {
  //     width: sizes.large,
  //     height: sizes.large,
  //     lineHeight: sizes.large,
  //     textAlign: "center",
  //     justifyContent: "center",
  //     alignItems: "center",
  //     textAlignVertical: "center",
  //     fontSize: 12,
  //     borderWidth: 0.5,
  //     borderColor: colors.trim,
  //     borderRadius: sizes.large / 2,
  //     marginHorizontal: sizes.tiny,
  //     overflow: "hidden",
  //   },
  //   mobileLargeBullet: {
  //     width: sizes.large * mobileIncrease,
  //     height: sizes.large * mobileIncrease,
  //     lineHeight: sizes.large * mobileIncrease,
  //     fontSize: 12 * mobileIncrease,
  //     borderRadius: (sizes.large / 2) * mobileIncrease,
  //     marginHorizontal: sizes.tiny * mobileIncrease,
  //   },
  //   largeBullet: {
  //     width: sizes.large * largeFactor,
  //     height: sizes.large * largeFactor,
  //     lineHeight: sizes.large * largeFactor,
  //     fontSize: 12 * largeFactor,
  //     borderRadius: (sizes.large / 2) * largeFactor,
  //     marginHorizontal: sizes.tiny * largeFactor,
  //   },
  //   smallBullet: {
  //     width: sizes.large * smallFactor,
  //     height: sizes.large * smallFactor,
  //     lineHeight: sizes.large * smallFactor,
  //     fontSize: 12 * smallFactor,
  //     borderRadius: (sizes.large / 2) * smallFactor,
  //     marginHorizontal: sizes.tiny * smallFactor,
  //   },
  //   hr: {
  //     width: "100%",
  //     height: 0,
  //     marginVertical: sizes.base,
  //     borderBottomWidth: 1,
  //     borderColor: colors.trim,
  //   },
});

// const test = () => {
//   return ENV.DARK;
// };

// export {
//   DARK,
//   WIDTH,
//   HEIGHT,
//   phi,
//   inversePhi,
//   mobileIncrease,
//   CHILD_RATIO,
//   PARENT_RATIO,
//   test,
// };
